// Ngăn chi tiết đơn vị của sơ đồ mạng lưới — phần khung dùng chung cho /npp/network và /network (Đại lý / CTV);
// nội dung (thông tin, nút, thẻ) do trang lắp.
//  · Máy tính: NetworkSidePanel — ngăn trượt từ phải, nằm TRONG khung cây (không phủ cả trang, không chặn cây).
//  · Điện thoại / iPad: NetworkSheet — ngăn trượt từ đáy có lớp phủ.
// Cả hai không phải hộp thoại Radix: nút trong ngăn mở hộp thoại của khu NPP (npp/legacy …/Modal, gắn vào <body>) —
// hộp thoại Radix (modal) khoá con trỏ + tiêu điểm ngoài nó nên hộp thoại kia không bấm / gõ được. Esc đóng ngăn,
// trừ khi đang có hộp thoại phía trên (Esc đó là của hộp thoại).

import { useEffect, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { ExternalLink, X } from 'lucide-react'
import type { NetworkCounts, NetworkUnitType } from '@shoptrack/common/network-tree'
import { cn } from '@/lib/utils'
import { NETWORK_TYPE_META } from './NetworkTreeRow'

/** Nhịp trượt của ngăn (vào nhanh, đỗ êm). */
const EASE = 'cubic-bezier(.2,.8,.2,1)'

/** Esc đóng ngăn — trừ khi Esc đang dành cho lớp khác: hộp thoại phía trên (khu NPP: [data-modal]; Radix: dialog đang
 *  mở), menu / ô thả (chuông thông báo), đang gõ trong ô nhập (Esc xoá ô tìm), hay ô xác nhận trong ngăn tự xử lý
 *  (preventDefault). */
export function useNetworkPanelEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (document.querySelector('[data-modal], [role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"], [data-radix-popper-content-wrapper]')) return
      if (e.target instanceof Element && e.target.closest('input, textarea, select, [contenteditable="true"]')) return
      onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, onClose])
}

/** Ngăn phải trên máy tính — đặt trong khung cây (relative). Đóng vẫn giữ nội dung cũ để trượt ra mượt. */
export function NetworkSidePanel({ open, width, label = 'Chi tiết đơn vị', children }: { open: boolean; width: number; label?: string; children: ReactNode }) {
  return (
    <aside
      aria-label={label}
      aria-hidden={!open || undefined}
      style={{ width, transform: open ? 'translateX(0)' : 'translateX(104%)', visibility: open ? 'visible' : 'hidden', transitionTimingFunction: EASE }}
      className="absolute inset-y-0 right-0 z-10 max-w-full overflow-y-auto overscroll-contain border-l border-rule bg-raise shadow-[-18px_0_44px_rgb(var(--ink)/0.12)] transition-[transform,visibility] duration-300"
    >
      {children}
    </aside>
  )
}

/** Ngăn đáy trên điện thoại / iPad: lớp phủ bấm để đóng, cao tối đa 88% màn, nội dung tự cuộn. */
export function NetworkSheet({ open, onClose, label = 'Chi tiết đơn vị', children }: { open: boolean; onClose: () => void; label?: string; children: ReactNode }) {
  useNetworkPanelEscape(open, onClose)
  // Gắn vào <body> như hộp thoại của khu NPP: vùng nội dung của khung là `isolate`, lớp phủ đặt bên trong bị thanh
  // đáy (fixed, z-40) đè lên. Lớp `app-content` giữ quy tắc CSS của vùng nội dung (tiêu đề, bảng).
  return createPortal(
    <div className="app-content">
      <button
        type="button"
        aria-label="Đóng"
        tabIndex={-1}
        onClick={onClose}
        disabled={!open}
        className={cn('fixed inset-0 z-[45] w-full touch-none bg-ink/40 transition-opacity duration-200', open ? 'opacity-100' : 'pointer-events-none opacity-0')}
      />
      <section
        role="dialog"
        aria-label={label}
        aria-hidden={!open || undefined}
        style={{ transform: open ? 'translateY(0)' : 'translateY(105%)', visibility: open ? 'visible' : 'hidden', transitionTimingFunction: EASE }}
        className="fixed inset-x-0 bottom-0 z-[45] max-h-[88dvh] overflow-y-auto overscroll-contain rounded-t-[20px] bg-raise pb-[env(safe-area-inset-bottom)] shadow-overlay transition-[transform,visibility] duration-300"
      >
        {children}
      </section>
    </div>,
    document.body,
  )
}

/** Ô vuông chữ tắt đầu ngăn (44px). `className` = màu: loại đơn vị (tone-… bg-toneBg text-toneFg) hoặc màu nhấn. */
export function NetworkPanelBadge({ short, className }: { short: string; className: string }) {
  return (
    <span aria-hidden className={cn('flex size-11 shrink-0 items-center justify-center rounded-[12px] text-xs font-bold', className)}>
      {short}
    </span>
  )
}

/** Nhãn viên thuốc dưới tên (loại, trạng thái, việc cần xử lý). */
export function NetworkPanelPill({ className, children }: { className: string; children: ReactNode }) {
  return <span className={cn('inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2 text-[11.5px] font-semibold', className)}>{children}</span>
}

/**
 * Đầu ngăn, dính trên cùng khi cuộn: (tay nắm) · chữ tắt · tên + nhãn · mở trang ↗ · đóng; `children` là phần dính
 * theo (đường dẫn, nút, thẻ).
 */
export function NetworkPanelHead({
  compact,
  badge,
  title,
  pills,
  openTo,
  openLabel = 'Mở trang đơn vị',
  onClose,
  children,
}: {
  compact: boolean
  badge: ReactNode
  title: string
  pills?: ReactNode
  /** Trang chi tiết đầy đủ (đường dẫn đã ở khu đúng, vd. /npp/units/:id). */
  openTo?: string
  openLabel?: string
  onClose: () => void
  children?: ReactNode
}) {
  const iconBtn = 'motion-state flex size-10 shrink-0 items-center justify-center rounded-[10px] text-inkSoft hover:bg-paperSoft hover:text-ink mobile:size-11'
  return (
    <div className="sticky top-0 z-[2] border-b border-rule bg-raise">
      {compact && (
        <div className="flex justify-center pt-2">
          <span aria-hidden className="h-1 w-10 rounded-full bg-rule" />
        </div>
      )}
      <div className={cn('flex items-start gap-3 pb-2.5 pr-2 pt-4', compact ? 'pl-4' : 'pl-5')}>
        {badge}
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold leading-6 text-ink [overflow-wrap:anywhere]">{title}</h2>
          {pills && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{pills}</div>}
        </div>
        {openTo && (
          <Link to={openTo} aria-label={openLabel} className={iconBtn}>
            <ExternalLink className="size-[18px]" aria-hidden />
          </Link>
        )}
        <button type="button" onClick={onClose} aria-label="Đóng" className={iconBtn}>
          <X className="size-[18px]" aria-hidden />
        </button>
      </div>
      {children}
    </div>
  )
}

export interface NetworkCrumb {
  key: string
  label: string
  /** Không có = mục cuối (chính đơn vị đang xem). */
  onPick?: () => void
}

/** Đường dẫn từ gốc tới đơn vị — bấm một cấp trên là chọn cấp đó trên cây. */
export function NetworkCrumbs({ items, compact }: { items: NetworkCrumb[]; compact: boolean }) {
  return (
    <nav aria-label="Đường dẫn" className={cn('flex flex-wrap items-center gap-x-1 gap-y-0.5 pb-2.5 text-[12.5px] text-muted', compact ? 'px-4' : 'px-5')}>
      {items.map((c) =>
        c.onPick ? (
          <span key={c.key} className="flex items-center gap-1">
            <button
              type="button"
              onClick={c.onPick}
              className="motion-state min-h-7 rounded-md px-1 text-inkSoft hover:bg-paperSoft hover:text-ink mobile:min-h-11"
            >
              {c.label}
            </button>
            <span aria-hidden>›</span>
          </span>
        ) : (
          <span key={c.key} aria-current="location" className="px-1 font-semibold text-ink">
            {c.label}
          </span>
        ),
      )}
    </nav>
  )
}

/** Thẻ của ngăn (Thông tin / Thành viên / …) — gạch chân màu nhấn ở thẻ đang mở. */
export function NetworkPanelTabs<T extends string>({
  tabs,
  value,
  onChange,
  compact,
  label = 'Mục của đơn vị',
}: {
  tabs: ReadonlyArray<readonly [T, string]>
  value: T
  onChange: (v: T) => void
  compact: boolean
  label?: string
}) {
  return (
    <div role="tablist" aria-label={label} className={cn('flex gap-0.5 overflow-x-auto', compact ? 'px-2' : 'px-3')}>
      {tabs.map(([k, l]) => (
        <button
          key={k}
          type="button"
          role="tab"
          aria-selected={value === k}
          onClick={() => onChange(k)}
          className={cn(
            'motion-state h-10 shrink-0 px-2.5 text-[13.5px] mobile:h-11',
            value === k ? 'font-semibold text-accent shadow-[inset_0_-2px_0_rgb(var(--accent))]' : 'font-medium text-inkSoft hover:text-ink',
          )}
        >
          {l}
        </button>
      ))}
    </div>
  )
}

/** Hàng ô số nhỏ trong ngăn — máy tính một hàng, ngăn gọn (điện thoại) hai cột. */
export function NetworkPanelStats({ items, compact }: { items: ReadonlyArray<{ label: string; value: ReactNode }>; compact: boolean }) {
  return (
    <div
      className={cn('grid gap-2', compact ? 'grid-cols-2' : '[grid-template-columns:repeat(var(--n),minmax(0,1fr))]')}
      style={{ '--n': items.length || 1 } as CSSProperties}
    >
      {items.map((s) => (
        <div key={s.label} className="min-w-0 rounded-[12px] bg-paperSoft px-3 py-2.5">
          <div className="truncate text-[17px] font-bold leading-[22px] tabular-nums text-ink">{s.value}</div>
          <div className="mt-0.5 truncate text-[11.5px] text-muted">{s.label}</div>
        </div>
      ))}
    </div>
  )
}

/** Ngăn tóm tắt ô gốc (NPP / chính Đại lý · CTV đang xem): số đơn vị theo loại + lối vào bộ lọc Cần xử lý. */
export function NetworkRootSummary({
  short,
  name,
  kind,
  counts,
  types,
  issueCount,
  onIssues,
  onClose,
  compact,
}: {
  short: string
  name: string
  /** Loại của ô gốc ("Nhà phân phối", "Đại lý", "CTV"). */
  kind: string
  counts: NetworkCounts
  /** Loại cấp dưới có thể có (NPP: cả ba; Đại lý: CTV + Shop; CTV: Shop). */
  types: readonly NetworkUnitType[]
  issueCount: number
  onIssues: () => void
  onClose: () => void
  compact: boolean
}) {
  const total = types.reduce((n, t) => n + counts[t], 0)
  return (
    <>
      <NetworkPanelHead
        compact={compact}
        badge={<NetworkPanelBadge short={short} className="bg-accent text-onAccent" />}
        title={name}
        pills={<NetworkPanelPill className="bg-paperSoft text-inkSoft">{kind} · {total.toLocaleString('vi-VN')} đơn vị</NetworkPanelPill>}
        onClose={onClose}
      />
      <div className={cn('flex flex-col gap-4 pb-5 pt-4', compact ? 'px-4' : 'px-5')}>
        <div className="grid grid-cols-3 gap-2 text-center">
          {types.map((t) => (
            <div key={t} className={cn(NETWORK_TYPE_META[t].tone, 'rounded-[12px] bg-toneBg px-2 py-3 text-toneFg')}>
              <p className="text-xl font-bold leading-[26px] tabular-nums">{counts[t].toLocaleString('vi-VN')}</p>
              <p className="text-xs">{NETWORK_TYPE_META[t].label}</p>
            </div>
          ))}
        </div>
        {issueCount > 0 && (
          <button
            type="button"
            onClick={onIssues}
            className="motion-state min-h-11 w-full rounded-lg bg-warn/10 px-3.5 text-left text-sm font-medium text-warn hover:bg-warn/15"
          >
            {issueCount.toLocaleString('vi-VN')} đơn vị cần xử lý
          </button>
        )}
      </div>
    </>
  )
}
